// src/pages/ItineraryBuilder/index.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  CircularProgress,
  Grid
} from '@mui/material';
import { 
  Save as SaveIcon,
  CalendarMonth as CalendarIcon,
  Email as EmailIcon,
  AutoAwesome as MagicIcon
} from '@mui/icons-material';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import { format, addDays } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

// Custom components
import TripSelector from '../../components/itinerary/TripSelector';
import DayPanel from '../../components/itinerary/DayPanel';
import ActivityList from '../../components/itinerary/ActivityList';
import ActivityForm from '../../components/itinerary/ActivityForm';

// Context
import { 
  ItineraryProvider,
  useItinerary,
  Activity,
  TimeSlot,
  ItineraryDay,
  Trip
} from '../../contexts/ItineraryContext';

// Icons for activity types
import {
  Schedule as ScheduleIcon,
  DirectionsCar as TransportIcon,
  Hotel as HotelIcon,
  Restaurant as RestaurantIcon,
  Attractions as AttractionIcon
} from '@mui/icons-material';

// Wrapper component that uses the provider
const ItineraryBuilderWithProvider: React.FC = () => {
  return (
    <ItineraryProvider>
      <ItineraryBuilderContent />
    </ItineraryProvider>
  );
};

// Main content component that uses the context
const ItineraryBuilderContent: React.FC = () => {
  // Get route params and navigate
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  
  // Get context data and functions
  const { 
    trips, 
    selectedTrip, 
    setSelectedTrip,
    itinerary,
    setItinerary,
    activities,
    setActivities,
    loading,
    error,
    fetchTripById,
    generateActivities,
    saveItinerary 
  } = useItinerary();
  
  // Component state
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [editActivityDialogOpen, setEditActivityDialogOpen] = useState(false);
  const [addActivityDialogOpen, setAddActivityDialogOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState<Activity | null>(null);
  const [isGeneratingActivities, setIsGeneratingActivities] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Initialize itinerary from URL param if provided
  useEffect(() => {
    if (tripId) {
      fetchTripById(tripId);
    }
  }, [tripId, fetchTripById]);
  
  // Initialize itinerary when trip is selected
  useEffect(() => {
    if (selectedTrip) {
      initializeItinerary(selectedTrip.startDate, selectedTrip.endDate);
    }
  }, [selectedTrip]);
  
  // Initialize itinerary with default time slots
  const initializeItinerary = (startDate: Date, endDate: Date) => {
    const days: ItineraryDay[] = [];
    let currentDate = new Date(startDate);
    let dayIndex = 0;
    
    while (currentDate <= endDate) {
      // Create default time slots for each day
      const defaultSlots: TimeSlot[] = [
        { id: `slot-${dayIndex}-1`, time: '07:00 AM', dayIndex, activityId: null },
        { id: `slot-${dayIndex}-2`, time: '09:00 AM', dayIndex, activityId: null },
        { id: `slot-${dayIndex}-3`, time: '12:00 PM', dayIndex, activityId: null },
        { id: `slot-${dayIndex}-4`, time: '03:00 PM', dayIndex, activityId: null },
        { id: `slot-${dayIndex}-5`, time: '06:00 PM', dayIndex, activityId: null },
        { id: `slot-${dayIndex}-6`, time: '08:00 PM', dayIndex, activityId: null }
      ];
      
      days.push({
        date: new Date(currentDate),
        slots: defaultSlots
      });
      
      currentDate = addDays(currentDate, 1);
      dayIndex++;
    }
    
    setItinerary(days);
  };
  
  // Get activity suggestions
  const handleGenerateActivities = async () => {
    if (!selectedTrip) return;
    
    setIsGeneratingActivities(true);
    const newActivities = await generateActivities(selectedTrip.destination);
    setIsGeneratingActivities(false);
    
    setSuccessMessage('New activities generated!');
  };
  
  // Handle drag and drop
  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    
    // Dropped outside a droppable area
    if (!destination) return;
    
    // If dropping from activity list to a time slot
    if (source.droppableId === 'activity-list' && destination.droppableId.startsWith('day-')) {
      const activityId = activities[source.index].id;
      const [, dayIndexStr, slotIndex] = destination.droppableId.split('-');
      const dayIndex = parseInt(dayIndexStr);
      
      // Update the itinerary
      const updatedItinerary = [...itinerary];
      updatedItinerary[dayIndex].slots[parseInt(slotIndex)].activityId = activityId;
      
      setItinerary(updatedItinerary);
    }
    // If reordering within a day's time slots or between days
    else if (source.droppableId.startsWith('day-') && destination.droppableId.startsWith('day-')) {
      const [, sourceDayIndexStr, sourceSlotIndex] = source.droppableId.split('-');
      const [, destDayIndexStr, destSlotIndex] = destination.droppableId.split('-');
      const sourceDayIndex = parseInt(sourceDayIndexStr);
      const destDayIndex = parseInt(destDayIndexStr);
      const sourceSlotIndexNum = parseInt(sourceSlotIndex);
      const destSlotIndexNum = parseInt(destSlotIndex);
      
      const updatedItinerary = [...itinerary];
      const activityId = updatedItinerary[sourceDayIndex].slots[sourceSlotIndexNum].activityId;
      
      // Only proceed if there's an activity in the source
      if (activityId) {
        // Remove from source
        updatedItinerary[sourceDayIndex].slots[sourceSlotIndexNum].activityId = null;
        
        // Add to destination
        updatedItinerary[destDayIndex].slots[destSlotIndexNum].activityId = activityId;
        
        setItinerary(updatedItinerary);
      }
    }
  };
  
  // Remove activity from a time slot
  const removeActivityFromSlot = (dayIndex: number, slotIndex: number) => {
    const updatedItinerary = [...itinerary];
    updatedItinerary[dayIndex].slots[slotIndex].activityId = null;
    setItinerary(updatedItinerary);
  };
  
  // Find activity by ID
  const findActivityById = (id: string): Activity | undefined => {
    return activities.find(activity => activity.id === id);
  };
  
  // Handle editing an activity
  const handleEditActivity = (activity: Activity) => {
    setEditingActivity(activity);
    setEditActivityDialogOpen(true);
  };
  
  // Save edited activity
  const handleSaveActivity = (updatedActivity: Activity) => {
    // Update in activities list
    const updatedActivities = activities.map(activity => 
      activity.id === updatedActivity.id ? updatedActivity : activity
    );
    
    setActivities(updatedActivities);
    setEditActivityDialogOpen(false);
    setAddActivityDialogOpen(false);
    setEditingActivity(null);
  };
  
  // Add custom activity
  const handleAddCustomActivity = () => {
    setEditingActivity(null);
    setAddActivityDialogOpen(true);
  };
  
  // Save new activity
  const handleSaveNewActivity = (newActivity: Activity) => {
    setActivities([...activities, newActivity]);
    setAddActivityDialogOpen(false);
  };
  
  // Save itinerary
  const handleSaveItinerary = async () => {
    if (!selectedTrip) return;
    
    const success = await saveItinerary(itinerary);
    
    if (success) {
      setSuccessMessage('Itinerary saved successfully!');
    }
  };
  
  // Handle toast close
  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
  };
  
  // Get icon for activity type
  const getActivityIcon = (type: string) => {
    switch(type) {
      case 'activity':
        return <AttractionIcon color="primary" />;
      case 'food':
        return <RestaurantIcon color="secondary" />;
      case 'accommodation':
        return <HotelIcon color="action" />;
      case 'transportation':
        return <TransportIcon color="info" />;
      default:
        return <ScheduleIcon />;
    }
  };
  
  // Add batch update helper
  const batchStateUpdates = (callback: () => void) => {
    callback();
  };
  
  // Update handleSelectTrip
  const handleSelectTrip = (trip: Trip | null) => {
    batchStateUpdates(() => {
      setSelectedTrip(trip);
      setSelectedDayIndex(0); // Reset to first day
    });
    
    // Update URL to include trip ID
    if (trip) {
      navigate(`/itinerary/${trip.id}`);
    }
  };
  
  return (
    <Box 
      sx={{ 
        position: 'relative',
        height: 'calc(100vh - 100px)', // Fixed height container for the whole page
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden' // Critical - prevents page jumping
      }}
    >
      <Typography variant="h4" gutterBottom>
        Itinerary Builder
      </Typography>
      
      {/* Trip Selector - Fixed height container */}
      <Box sx={{ 
        mb: 3, 
        height: loading || !selectedTrip ? 'auto' : '100px',
        overflow: 'hidden',
        transition: 'height 0.3s ease' 
      }}>
        <TripSelector
          trips={trips}
          selectedTrip={selectedTrip}
          loading={loading}
          onSelectTrip={handleSelectTrip}
        />
      </Box>
      
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Main Content - Takes remaining height with fixed positioning */}
      <Box 
        sx={{ 
          flex: 1,
          display: selectedTrip ? 'block' : 'none',
          opacity: selectedTrip ? 1 : 0,
          transition: 'opacity 0.3s ease',
          position: 'relative',
          overflow: 'hidden' // Prevent content expansion
        }}
      >
        {selectedTrip && (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Box sx={{ 
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <Paper sx={{ 
                p: 2, 
                mb: 3,
                flexShrink: 0 // Prevent shrinking
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5">
                    Building Itinerary for {selectedTrip.title}
                  </Typography>
                  
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveItinerary}
                  >
                    Save Itinerary
                  </Button>
                </Box>
                
                <Box sx={{ 
                  display: 'flex', 
                  mb: 2, 
                  borderBottom: 1, 
                  borderColor: 'divider',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  backgroundColor: 'background.paper'
                }}>
                  {itinerary.map((_, index) => (
                    <Button
                      key={index}
                      variant={selectedDayIndex === index ? "contained" : "text"}
                      onClick={() => setSelectedDayIndex(index)}
                      sx={{ 
                        minWidth: 'auto', 
                        px: 2,
                        height: '40px'
                      }}
                    >
                      Day {index + 1}
                    </Button>
                  ))}
                </Box>
              </Paper>
              
              <Box sx={{ 
                flex: 1,
                overflow: 'hidden', // This is critical for preventing content expansion
                display: 'flex' // Use flexbox for the grid instead of Grid component
              }}>
                {/* Left Panel: Itinerary Timeline */}
                <Box sx={{ 
                  width: '58%', 
                  pr: 3,
                  height: '100%',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {itinerary.map((day, dayIndex) => (
                    <Box 
                      key={`day-${dayIndex}`}
                      sx={{ 
                        display: selectedDayIndex === dayIndex ? 'block' : 'none',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: selectedDayIndex === dayIndex ? 1 : 0,
                        visibility: selectedDayIndex === dayIndex ? 'visible' : 'hidden',
                        transition: 'opacity 0.2s ease',
                        overflow: 'auto' // Allow scrolling within the day panel
                      }}
                    >
                      <DayPanel
                        day={day}
                        dayIndex={dayIndex}
                        getActivityIcon={getActivityIcon}
                        findActivityById={findActivityById}
                        removeActivityFromSlot={removeActivityFromSlot}
                      />
                    </Box>
                  ))}
                </Box>
                
                {/* Right Panel: Activity Suggestions */}
                <Box sx={{ 
                  width: '42%', 
                  height: '100%',
                  overflow: 'hidden'
                }}>
                  <Paper sx={{ 
                    p: 2, 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                  }}>
                    <ActivityList
                      activities={activities}
                      isGenerating={isGeneratingActivities}
                      destination={selectedTrip.destination}
                      onGenerateActivities={handleGenerateActivities}
                      onEditActivity={handleEditActivity}
                      onAddCustomActivity={handleAddCustomActivity}
                    />
                  </Paper>
                </Box>
              </Box>
            </Box>
          </DragDropContext>
        )}
      </Box>
      
      {/* Edit Activity Dialog */}
      <Dialog 
        open={editActivityDialogOpen} 
        onClose={() => setEditActivityDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Activity</DialogTitle>
        <DialogContent>
          {editingActivity && selectedTrip && (
            <ActivityForm
              activity={editingActivity}
              destination={selectedTrip.destination}
              onSave={handleSaveActivity}
              onCancel={() => setEditActivityDialogOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Add Custom Activity Dialog */}
      <Dialog 
        open={addActivityDialogOpen} 
        onClose={() => setAddActivityDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Custom Activity</DialogTitle>
        <DialogContent>
          {selectedTrip && (
            <ActivityForm
              destination={selectedTrip.destination}
              onSave={handleSaveNewActivity}
              onCancel={() => setAddActivityDialogOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
      
      {/* Success Notification */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        message={successMessage}
      />
    </Box>
  );
};

// Export the wrapped component
export default ItineraryBuilderWithProvider;